// FILE: src/components/case-studies/admin/CasePreview.js
// Learner-style preview. Consumes the SAME learner-safe runtime shape the
// learner runner will use (buildLearnerPreview mirrors get_case_runtime /
// the attempt snapshot: {case, stages:[{sections, artifacts, questions}]}).
// No correct answers, rationales, or author notes exist in this payload —
// and the editor never touches case_attempt_answer_keys at all.

import React, { useMemo, useState } from 'react';
import { buildLearnerPreview } from './caseAdminUtils';
import { S, T } from './caseAdminStyles';

export default function CasePreview({ full }) {
  const payload = useMemo(() => buildLearnerPreview(full), [full]);
  const [upTo, setUpTo] = useState(1);
  const [artifact, setArtifact] = useState(null);
  const maxStage = payload.stages.length;
  const visible = payload.stages.filter((s) => s.sequence <= upTo);
  const activeQuestions = (payload.stages.find((s) => s.sequence === upTo) || { questions: [] }).questions;

  return (
    <div data-testid="case-preview">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
        <span style={S.sub}>Progressive-disclosure preview — step through stages as a learner would:</span>
        {payload.stages.map((s) => (
          <button key={s.id} style={{ ...S.btn, ...(s.sequence === upTo ? { background: T.brand, borderColor: T.brand, color: '#fff' } : {}) }}
            data-testid={`preview-stage-${s.sequence}`} onClick={() => { setUpTo(s.sequence); setArtifact(null); }}>
            Stage {s.sequence}
          </button>
        ))}
        {upTo < maxStage && (
          <span style={S.chip(T.brandSoft, T.brand)} data-testid="preview-locked-note">
            {maxStage - upTo} stage(s) still locked at this point
          </span>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 58fr) minmax(0, 42fr)', gap: 14 }}>
        {/* Case workspace (left ~58%) */}
        <div style={{ ...S.panel, maxHeight: '68vh', overflowY: 'auto' }} data-testid="preview-case-panel">
          <h2 style={{ ...S.h1 }}>{payload.case.title}</h2>
          <div style={{ ...S.sub, marginBottom: 10 }}>
            You are: {payload.case.learner_role || '—'} · ~{payload.case.estimated_minutes || '?'} min · {payload.case.difficulty || '—'}
          </div>
          {visible.map((st) => (
            <div key={st.id} style={{ marginBottom: 14 }}>
              {st.sequence > 1 && (
                <div style={{ ...S.chip(T.brandSoft, T.brand), marginBottom: 8 }} data-testid={`preview-new-info-${st.sequence}`}>
                  ✦ New case information — {st.title}
                </div>
              )}
              {st.intro && <div style={{ fontSize: '0.86rem', fontStyle: 'italic', color: T.muted, marginBottom: 8 }}>{st.intro}</div>}
              {st.sections.map((sec) => (
                <div key={sec.id} style={{ marginBottom: 10 }}>
                  <div style={{ fontSize: '0.78rem', fontWeight: 800, textTransform: 'uppercase', color: T.muted }}>{sec.title}</div>
                  <div style={{ fontSize: '0.86rem', whiteSpace: 'pre-wrap' }}>{sec.content}</div>
                </div>
              ))}
              {st.artifacts.length > 0 && (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {st.artifacts.map((a) => (
                    <button key={a.id} style={{ ...S.btn, fontSize: '0.76rem' }} data-testid={`preview-artifact-${a.id}`}
                      onClick={() => setArtifact(a)}>
                      📎 {a.title}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
          {artifact && <ArtifactView artifact={artifact} onClose={() => setArtifact(null)} />}
        </div>

        {/* Question workspace (right ~42%) */}
        <div style={{ ...S.panel, maxHeight: '68vh', overflowY: 'auto' }} data-testid="preview-question-panel">
          <div style={{ ...S.sub, marginBottom: 8 }}>Questions in the active stage (learner-safe: no keys, no rationales):</div>
          {activeQuestions.length === 0 && <div style={S.sub}>No questions in this stage.</div>}
          {activeQuestions.map((q) => (
            <div key={q.id} style={{ border: `1px solid ${T.line}`, borderRadius: 8, padding: 12, marginBottom: 10 }}>
              <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                <strong style={{ fontSize: '0.8rem' }}>Q{q.case_sequence}</strong>
                <span style={S.chip(T.line, T.muted)}>{q.type}</span>
              </div>
              <div style={{ fontSize: '0.88rem', marginBottom: 8 }}>{q.question}</div>
              {q.image && q.image.src && (
                <img src={q.image.src} alt={q.image.alt || ''} style={{ maxWidth: '100%', borderRadius: 8, marginBottom: 8 }} />
              )}
              <PreviewAnswers q={q} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function PreviewAnswers({ q }) {
  const c = q.choices;
  if (Array.isArray(c)) {
    const multi = q.type === 'multiple_select';
    return c.map((ch) => (
      <label key={ch.id} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '5px 0', fontSize: '0.84rem' }}>
        <input type={multi ? 'checkbox' : 'radio'} disabled name={`prev-${q.id}`} /> {ch.id}. {ch.text}
      </label>
    ));
  }
  if (c && Array.isArray(c.prompts)) {
    return c.prompts.map((p) => (
      <div key={p.id} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '4px 0', fontSize: '0.84rem' }}>
        <span style={{ flex: 1 }}>{p.text}</span>
        <select disabled style={{ padding: '3px 6px' }}>
          <option>Match…</option>
          {(c.options || []).map((o) => <option key={o.id}>{o.text}</option>)}
        </select>
      </div>
    ));
  }
  if (c && Array.isArray(c.items)) {
    return c.items.map((it, i) => (
      <div key={it.id} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '4px 0', fontSize: '0.84rem' }}>
        <span style={{ color: 'var(--muted)' }}>{i + 1}.</span> <span style={{ flex: 1 }}>{it.text}</span>
        <button style={{ padding: '1px 7px' }} disabled>↑</button><button style={{ padding: '1px 7px' }} disabled>↓</button>
      </div>
    ));
  }
  return <div style={S.sub}>Unsupported answer shape (this type is publish-blocked).</div>;
}

function ArtifactView({ artifact, onClose }) {
  const cj = artifact.content_json;
  return (
    <div style={{ border: `1px solid ${T.lineStrong}`, borderRadius: 8, padding: 12, marginTop: 8, background: T.bg }}
      data-testid="preview-artifact-view">
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
        <strong style={{ fontSize: '0.85rem' }}>📎 {artifact.title}</strong>
        <span style={{ ...S.chip(T.line, T.muted), marginLeft: 8 }}>{artifact.artifact_type}</span>
        <span style={{ flex: 1 }} />
        <button style={S.btnGhost} onClick={onClose}>Close</button>
      </div>
      {artifact.description && <div style={{ ...S.sub, marginBottom: 8 }}>{artifact.description}</div>}
      {artifact.asset_url && <img src={artifact.asset_url} alt={artifact.alt_text || ''} style={{ maxWidth: '100%', borderRadius: 8 }} />}
      {cj && Array.isArray(cj.columns) && Array.isArray(cj.rows) && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem', background: '#fff' }}>
          <thead><tr>{cj.columns.map((col) => (
            <th key={col.key} scope="col" style={{ textAlign: 'left', padding: '6px 8px', borderBottom: `2px solid ${T.lineStrong}` }}>{col.label}</th>
          ))}</tr></thead>
          <tbody>{cj.rows.map((row, i) => (
            <tr key={i}>{cj.columns.map((col) => (
              <td key={col.key} style={{ padding: '5px 8px', borderBottom: `1px solid ${T.line}` }}>{String(row[col.key] ?? '')}</td>
            ))}</tr>
          ))}</tbody>
        </table>
      )}
      {cj && cj.subject !== undefined && (
        <div style={{ background: '#fff', borderRadius: 8, padding: 10, fontSize: '0.82rem' }}>
          <div><strong>From:</strong> {cj.from}</div>
          <div><strong>To:</strong> {cj.to}</div>
          <div><strong>Subject:</strong> {cj.subject}</div>
          <hr style={{ border: 'none', borderTop: `1px solid ${T.line}` }} />
          <div style={{ whiteSpace: 'pre-wrap' }}>{cj.body}</div>
        </div>
      )}
      {cj && cj.body !== undefined && cj.subject === undefined && (
        <div style={{ whiteSpace: 'pre-wrap', fontSize: '0.84rem' }}>{cj.body}</div>
      )}
    </div>
  );
}
